import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { FC, MouseEventHandler, PropsWithChildren } from 'react';

import { NotIOsAppFeature } from '@sorare/core/src/components/BuyableFeature';
import { useLoggedCallback } from '@sorare/core/src/hooks/useLoggedCallback';
import { ClickAddToCart_Origin } from '@sorare/core/src/protos/events/so5/shared/events';

import { useMarketplaceContext } from 'contexts/Marketplace';
import { useCartEvents } from 'hooks/events/useCartEvents';
import { useCart } from 'hooks/useCart';

import type { AddToCartOnClickWrapper_anyCard } from './__generated__/index.graphql';
import { useEligibleForCartPromo } from './useEligibleForCartPromo';

type Props = {
  anyCard: AddToCartOnClickWrapper_anyCard;
  origin: ClickAddToCart_Origin;
  onSuccess?: () => void;
  disablePopin?: boolean;
  children: FC<
    PropsWithChildren<{
      onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
      loading: boolean;
      inCart: boolean;
      isEligibleForCartPromo?: boolean;
    }>
  >;
};

export const AddToCartOnClickWrapper = ({
  anyCard,
  origin = ClickAddToCart_Origin.MARKETPLACE,
  children,
  disablePopin = false,
  onSuccess,
}: Props) => {
  const { trackClickAddToCart } = useCartEvents({ anyCard });
  const { addCardsToCart, similarCardisInCart } = useCart();
  const { showAddToCartPopin } = useMarketplaceContext();
  const { eligibleForCartPromo } = useEligibleForCartPromo();
  const onClick = useLoggedCallback(() => {
    trackClickAddToCart(origin);
    addCardsToCart.mutate(anyCard.sport, [anyCard.slug]).then(cart => {
      const slug =
        cart?.mainCartItems?.find(ci => ci.card.slug === anyCard.slug)?.card
          .slug || undefined;
      if (slug && !disablePopin) {
        showAddToCartPopin([slug]);
      }
      onSuccess?.();
    });
  });

  const inCart = !!similarCardisInCart(anyCard);

  const isEligibleForCartPromo = eligibleForCartPromo(anyCard);

  return (
    <NotIOsAppFeature exceptMarketAuthorizedIosApp>
      {children({
        loading: addCardsToCart.loading,
        onClick,
        inCart,
        isEligibleForCartPromo,
      })}
    </NotIOsAppFeature>
  );
};

AddToCartOnClickWrapper.fragments = {
  anyCard: gql`
    fragment AddToCartOnClickWrapper_anyCard on AnyCardInterface {
      slug
      ...useCart_anyCard
      ...useCartEvents_anyCard
      ...useEligibleForCartPromo_anyCard
    }
    ${useCart.fragments.anyCard}
    ${useCartEvents.fragments.anyCard}
    ${useEligibleForCartPromo.fragments.anyCard}
  ` as TypedDocumentNode<AddToCartOnClickWrapper_anyCard>,
};
